import styled from 'styled-components';
import type { DefaultTheme } from 'styled-components';
import type { ReactNode } from 'react';

type Tone = 'neutral' | 'cyan' | 'magenta' | 'amber' | 'brand';

export interface BadgeProps {
  tone?: Tone;
  children: ReactNode;
}

type ToneResolver = (p: { theme: DefaultTheme }) => string;

/**
 * The tone names are historical (they date from the teal era and are used at
 * ~40 call sites), but what they RESOLVE to changed in 1.25.00.
 *
 * Before: cyan, magenta, amber and brand all resolved to the same purple — four
 * names for one colour, so picking a tone did nothing visible. Now there are
 * three real tones: neutral for a label, accent for something on the record,
 * and review for Council state, which is a reserved hue no preset can repaint.
 * Renaming the keys is a mechanical follow-up; changing what they mean is the
 * fix.
 */
const toneColor: Record<Tone, ToneResolver> = {
  neutral: ({ theme }) => theme.color.textMuted,
  cyan: ({ theme }) => theme.color.accentCyan,
  magenta: ({ theme }) => theme.color.textMuted,
  amber: ({ theme }) => theme.color.review,
  brand: ({ theme }) => theme.color.accentCyan,
};

const StyledBadge = styled.span<{ $tone: Tone }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  /* Body face, not mono: mono is for DATA (handles, counts, times, ids), and a
     badge carries a word. A number inside one still opts into tabular figures. */
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 500;
  padding: 3px 8px;
  border-radius: ${({ theme }) => theme.radius.sm};
  color: ${(p) => toneColor[p.$tone](p)};
  border: 1px solid
    ${(p) =>
      p.$tone === 'neutral' || p.$tone === 'magenta'
        ? `color-mix(in srgb, ${toneColor[p.$tone](p)} 50%, transparent)`
        : 'transparent'};
  background: color-mix(in srgb, ${(p) => toneColor[p.$tone](p)} 12%, transparent);
`;

export function Badge({ tone = 'neutral', children }: BadgeProps) {
  return <StyledBadge $tone={tone}>{children}</StyledBadge>;
}
